import React, { useEffect } from 'react';
import { Zap, Radio, Mic, Flag, ShieldCheck } from 'lucide-react';

interface AnimationProps {
  x: string;
  y: string;
  onComplete: () => void;
}

export const BroadcastAnimation: React.FC<AnimationProps> = ({ x, y, onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 2500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="absolute z-[9999] pointer-events-none flex items-center justify-center" style={{ top: y, left: x, transform: 'translate(-50%, -50%)' }}>
      {/* Massive Shockwaves */}
      <div className="absolute w-[500px] h-[500px] rounded-full border-[20px] border-green-500/50 animate-ping" style={{ animationDuration: '1.5s' }} />
      <div className="absolute w-[400px] h-[400px] rounded-full border-[15px] border-green-400/60 animate-ping" style={{ animationDuration: '1.2s', animationDelay: '0.2s' }} />
      <div className="absolute w-[300px] h-[300px] rounded-full border-[10px] border-green-300/70 animate-ping" style={{ animationDuration: '1s', animationDelay: '0.4s' }} />
      
      {/* Core Icon */}
      <div className="relative z-50 bg-black/50 rounded-full p-4 backdrop-blur-sm">
        <Radio className="w-24 h-24 text-green-400 animate-bounce drop-shadow-[0_0_30px_rgba(74,222,128,1)]" />
      </div>

      {/* Floating Text */}
      <div className="absolute -top-32 flex flex-col items-center animate-float-up z-[100]">
        <div className="text-3xl font-black text-white bg-green-600 px-6 py-3 rounded-xl border-4 border-white shadow-[0_0_30px_rgba(34,197,94,0.8)] whitespace-nowrap">
          BROADCASTING!
        </div>
        <div className="text-2xl font-bold text-green-300 mt-2 drop-shadow-lg">
          +2 TRUST
        </div>
      </div>
    </div>
  );
};

export const JamAnimation: React.FC<AnimationProps> = ({ x, y, onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 2500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="absolute z-[9999] pointer-events-none flex items-center justify-center" style={{ top: y, left: x, transform: 'translate(-50%, -50%)' }}>
       {/* Flash Background */}
       <div className="absolute w-[600px] h-[600px] bg-red-500/20 rounded-full animate-pulse" />
       
       {/* Electrical Arcs (Simulated with borders) */}
       <div className="absolute w-[400px] h-[400px] border-4 border-dashed border-yellow-400 rounded-full animate-spin" style={{ animationDuration: '3s' }} />
       <div className="absolute w-[350px] h-[350px] border-4 border-dashed border-red-500 rounded-full animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }} />

       {/* Main Bolt */}
       <div className="relative z-50 bg-black/50 rounded-full p-4 backdrop-blur-sm">
         <Zap className="w-32 h-32 text-yellow-400 animate-bounce drop-shadow-[0_0_50px_rgba(250,204,21,1)]" />
       </div>
       
       {/* Secondary Bolts */}
       <Zap className="absolute w-24 h-24 text-red-500 animate-ping drop-shadow-[0_0_20px_rgba(239,68,68,1)] z-40 -top-20 -left-20" style={{ animationDuration: '0.5s' }} />
       <Zap className="absolute w-24 h-24 text-red-500 animate-ping drop-shadow-[0_0_20px_rgba(239,68,68,1)] z-40 -bottom-20 -right-20" style={{ animationDuration: '0.7s' }} />

       {/* Floating Text */}
       <div className="absolute -top-32 flex flex-col items-center animate-float-up z-[100]">
        <div className="text-3xl font-black text-white bg-red-600 px-6 py-3 rounded-xl border-4 border-white shadow-[0_0_30px_rgba(239,68,68,0.8)] whitespace-nowrap">
          JAMMING!
        </div>
        <div className="text-2xl font-bold text-red-300 mt-2 drop-shadow-lg">
          THREAT NEUTRALIZED
        </div>
      </div>
    </div>
  );
};

export const ListenAnimation: React.FC<AnimationProps> = ({ x, y, onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 2500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="absolute z-[9999] pointer-events-none flex items-center justify-center" style={{ top: y, left: x, transform: 'translate(-50%, -50%)' }}>
      {/* Radar Waves */}
      <div className="absolute w-[400px] h-[400px] border-[4px] border-blue-500/50 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
      <div className="absolute w-[300px] h-[300px] border-[4px] border-blue-400/60 rounded-full animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }} />
      <div className="absolute w-[200px] h-[200px] border-[4px] border-blue-300/70 rounded-full animate-ping" style={{ animationDuration: '2s', animationDelay: '1s' }} />
      
      {/* Icon */}
      <div className="relative z-50 bg-black/50 rounded-full p-4 backdrop-blur-sm">
        <Mic className="w-24 h-24 text-blue-400 animate-bounce drop-shadow-[0_0_30px_rgba(96,165,250,1)]" />
      </div>

      {/* Floating Text */}
      <div className="absolute -top-32 flex flex-col items-center animate-float-up z-[100]">
        <div className="text-3xl font-black text-white bg-blue-600 px-6 py-3 rounded-xl border-4 border-white shadow-[0_0_30px_rgba(59,130,246,0.8)] whitespace-nowrap">
          LISTENING...
        </div>
        <div className="text-2xl font-bold text-blue-300 mt-2 drop-shadow-lg">
          +1 INTEL
        </div>
      </div>
    </div>
  );
};

export const RallyAnimation: React.FC<AnimationProps> = ({ x, y, onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 2500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="absolute z-[9999] pointer-events-none flex items-center justify-center" style={{ top: y, left: x, transform: 'translate(-50%, -50%)' }}>
      {/* Gathering Effect (Reverse Ping / Scale In) */}
      <div className="absolute w-[500px] h-[500px] border-[20px] border-yellow-500/30 rounded-full animate-ping" style={{ animationDuration: '3s' }} />
      
      {/* Starburst */}
      <div className="absolute w-[400px] h-[400px] bg-yellow-500/10 rounded-full animate-pulse" />
      
      {/* Icon */}
      <div className="relative z-50 bg-black/50 rounded-full p-4 backdrop-blur-sm">
        <Flag className="w-24 h-24 text-yellow-400 animate-bounce drop-shadow-[0_0_30px_rgba(250,204,21,1)]" />
      </div>

      {/* Floating Text */}
      <div className="absolute -top-32 flex flex-col items-center animate-float-up z-[100]">
        <div className="text-3xl font-black text-white bg-yellow-600 px-6 py-3 rounded-xl border-4 border-white shadow-[0_0_30px_rgba(234,179,8,0.8)] whitespace-nowrap">
          RALLYING!
        </div>
        <div className="text-2xl font-bold text-yellow-300 mt-2 drop-shadow-lg">
          +2 MORALE
        </div>
      </div>
    </div>
  );
};

export const SecureAnimation: React.FC<AnimationProps> = ({ x, y, onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 2500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="absolute z-[9999] pointer-events-none flex items-center justify-center" style={{ top: y, left: x, transform: 'translate(-50%, -50%)' }}>
      {/* Shield Effect */}
      <div className="absolute w-[400px] h-[400px] border-[10px] border-blue-500/50 rounded-full animate-pulse" />
      <div className="absolute w-[350px] h-[350px] border-[5px] border-blue-400/60 rounded-full animate-spin" style={{ animationDuration: '4s' }} />
      
      {/* Icon */}
      <div className="relative z-50 bg-black/50 rounded-full p-4 backdrop-blur-sm">
        <ShieldCheck className="w-24 h-24 text-blue-400 animate-bounce drop-shadow-[0_0_30px_rgba(96,165,250,1)]" />
      </div>

      {/* Floating Text */}
      <div className="absolute -top-32 flex flex-col items-center animate-float-up z-[100]">
        <div className="text-3xl font-black text-white bg-blue-600 px-6 py-3 rounded-xl border-4 border-white shadow-[0_0_30px_rgba(59,130,246,0.8)] whitespace-nowrap">
          SECURING...
        </div>
        <div className="text-2xl font-bold text-blue-300 mt-2 drop-shadow-lg">
          +5 DEFENSE
        </div>
      </div>
    </div>
  );
};

interface ActionAnimationsProps {
  type: string;
  x?: string;
  y?: string;
  onComplete?: () => void;
}

export const ActionAnimations: React.FC<ActionAnimationsProps> = ({ type, x = '50%', y = '50%', onComplete = () => {} }) => {
  switch (type) {
    case 'Broadcast': return <BroadcastAnimation x={x} y={y} onComplete={onComplete} />;
    case 'Jam': return <JamAnimation x={x} y={y} onComplete={onComplete} />;
    case 'Listen': return <ListenAnimation x={x} y={y} onComplete={onComplete} />;
    case 'Rally': return <RallyAnimation x={x} y={y} onComplete={onComplete} />;
    case 'Secure': return <SecureAnimation x={x} y={y} onComplete={onComplete} />;
    default: return null;
  }
};
